import axios, { type AxiosError } from 'axios';
import { TOKEN_STORAGE_KEY } from '../config';
import type {
  ApiKeysResponse,
  DashboardResponse,
  DisputeRow,
  KybDocumentsMap,
  LoginResponse,
  RegisterResponse,
  MeResponse,
  PortalEnvironment,
  PayoutRequest,
  TeamMember,
  WebhookLog,
  MerchantLocation,
  PosTerminal,
  ActivationTokenResponse,
  SettlementPreview,
  WalletChangelogEntry,
} from './types';

/** Production BFF origin (Cloudflare Pages has no Vite proxy). */
const PRODUCTION_API_ORIGIN = 'https://api.onyxpay.biz';

/**
 * Dev: `/v1` → Vite proxy → localhost:3000.
 * Prod: absolute API host so requests never hit the Pages origin.
 * Override anytime via VITE_API_BASE_URL (full origin, with or without `/v1`).
 */
export function resolveApiBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;

  const isDevelopment =
    import.meta.env.DEV || import.meta.env.MODE === 'development';

  if (isDevelopment) return '/v1';
  return PRODUCTION_API_ORIGIN;
}

export const API_BASE_URL = resolveApiBaseUrl();

/** Path under /v1/portal — avoids double `/v1/v1` when base URL already ends with `/v1`. */
export function portalPath(subpath: string): string {
  const rest = subpath.startsWith('/') ? subpath : `/${subpath}`;
  const portal = rest.startsWith('/portal') ? rest : `/portal${rest}`;
  const base = API_BASE_URL.replace(/\/$/, '');
  if (base === '/v1' || base.endsWith('/v1')) {
    return portal;
  }
  return `/v1${portal}`;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((req) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      const path = window.location.pathname;
      if (!path.startsWith('/login') && !path.startsWith('/register')) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);

export function getApiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<{ error?: string }>;
    return ax.response?.data?.error || ax.message || 'Request failed';
  }
  return err instanceof Error ? err.message : 'Request failed';
}

export type EmailCheckResponse = {
  ok: boolean;
  email?: string;
  email_taken?: boolean;
  available?: boolean;
  portal_user?: boolean;
  error?: string;
};

export const portalApi = {
  checkEmail: (email: string) =>
    apiClient
      .get<EmailCheckResponse>(portalPath('/auth/check-email'), {
        params: { email: email.trim().toLowerCase() },
      })
      .then((r) => r.data),

  register: (body: { company_name: string; email: string; password: string }) =>
    apiClient.post<RegisterResponse>(portalPath('/auth/register'), body).then((r) => r.data),

  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>(portalPath('/auth/login'), { email, password }).then((r) => r.data),

  verify2fa: (code: string, pending_token: string) =>
    apiClient.post<LoginResponse>(portalPath('/auth/verify-2fa'), { code, pending_token }).then((r) => r.data),

  setup2fa: () =>
    apiClient
      .get<{ ok: boolean; secret: string; otpauth_url: string }>(portalPath('/auth/2fa/setup'))
      .then((r) => r.data),

  enable2fa: (code: string) =>
    apiClient.post<{ ok: boolean }>(portalPath('/auth/2fa/enable'), { code }).then((r) => r.data),

  me: () => apiClient.get<MeResponse>(portalPath('/me')).then((r) => r.data),

  kybCorporate: (body: Record<string, string | undefined>) =>
    apiClient.patch(portalPath('/kyb/corporate'), body).then((r) => r.data),

  kybUbo: (body: Record<string, string | undefined>) =>
    apiClient.patch(portalPath('/kyb/ubo'), body).then((r) => r.data),

  kybDocuments: () =>
    apiClient
      .get<{ ok: boolean; documents: KybDocumentsMap }>(portalPath('/kyb/documents'))
      .then((r) => r.data),

  kybDocument: (body: {
    document_type: string;
    mime_type: string;
    file_base64: string;
    file_name: string;
  }) => apiClient.post(portalPath('/kyb/documents'), body).then((r) => r.data),

  dashboard: (environment: PortalEnvironment, range: string) =>
    apiClient
      .get<DashboardResponse>(portalPath('/dashboard'), { params: { environment, range } })
      .then((r) => r.data),

  listPayouts: () =>
    apiClient.get<{ ok: boolean; payout_requests: PayoutRequest[] }>(portalPath('/payouts')).then((r) => r.data),

  createPayout: (body: Record<string, unknown>) =>
    apiClient
      .post<{ ok: boolean; payout_request: PayoutRequest; fee_display: string; net_display: string }>(
        portalPath('/payouts'),
        body,
      )
      .then((r) => r.data),

  apiKeys: (environment: PortalEnvironment) =>
    apiClient.get<ApiKeysResponse>(portalPath('/api-keys'), { params: { environment } }).then((r) => r.data),

  revealSecret: (password: string, totp_code: string, environment: PortalEnvironment) =>
    apiClient
      .post<ApiKeysResponse>(portalPath('/api-keys/reveal'), { password, totp_code, environment })
      .then((r) => r.data),

  regenerateKeys: (totp_code: string, environment: PortalEnvironment) =>
    apiClient
      .post<ApiKeysResponse>(portalPath('/api-keys/regenerate'), { totp_code, environment })
      .then((r) => r.data),

  updateWebhook: (webhook_url: string) =>
    apiClient.patch<{ ok: boolean; webhook_url: string }>(portalPath('/webhook'), { webhook_url }).then((r) => r.data),

  testWebhook: () =>
    apiClient
      .post<{ ok: boolean; status_code: number; success: boolean; response_ms: number }>(portalPath('/webhook/test'))
      .then((r) => r.data),

  webhookLogs: () =>
    apiClient.get<{ ok: boolean; logs: WebhookLog[] }>(portalPath('/webhook/logs')).then((r) => r.data),

  listDisputes: () =>
    apiClient.get<{ ok: boolean; disputes: DisputeRow[] }>(portalPath('/disputes')).then((r) => r.data),

  uploadDisputeEvidence: (disputeId: string, evidence_url?: string) =>
    apiClient.post(portalPath(`/disputes/${disputeId}/evidence`), { evidence_url }).then((r) => r.data),

  listTeam: () => apiClient.get<{ ok: boolean; members: TeamMember[] }>(portalPath('/team')).then((r) => r.data),

  createTeamMember: (body: { email: string; password: string; role: string; full_name?: string }) =>
    apiClient.post(portalPath('/team'), body).then((r) => r.data),

  getSettlementWallet: () =>
    apiClient
      .get<{ ok: boolean; settlement: SettlementPreview }>(portalPath('/settlement-wallet'))
      .then((r) => r.data),

  getSettlementWalletChangelog: (limit = 50) =>
    apiClient
      .get<{ ok: boolean; changelog: WalletChangelogEntry[] }>(
        portalPath('/settlement-wallet/changelog'),
        { params: { limit } },
      )
      .then((r) => r.data),

  updateSettlementWallet: (solana_wallet: string, totp_code?: string) =>
    apiClient
      .patch<{ ok: boolean; solana_wallet: string; merchant_vault: string; message?: string }>(
        portalPath('/settlement-wallet'),
        { solana_wallet, ...(totp_code ? { totp_code } : {}) },
      )
      .then((r) => r.data),

  listLocations: () =>
    apiClient.get<{ ok: boolean; locations: MerchantLocation[] }>(portalPath('/locations')).then((r) => r.data),

  listTerminals: () =>
    apiClient.get<{ ok: boolean; terminals: PosTerminal[] }>(portalPath('/terminals')).then((r) => r.data),

  createTerminalActivationToken: (body: {
    location_id?: string;
    location_name?: string;
    default_fiat_currency?: string;
  }) =>
    apiClient
      .post<ActivationTokenResponse>(portalPath('/terminals/activation-token'), body)
      .then((r) => r.data),
};
